import type React from "react";

import type { SimpleProjectDto } from "@/api/types";
import { FilterFunnel02Icon } from "@/components/Icons/FilterFunnel02Icon";
import { InfoCircleIcon } from "@/components/Icons/InfoCircleIcon";
import { LayoutLeftIcon } from "@/components/Icons/LayoutLeftIcon";
import { Button } from "@/design-system/Button";
import { Heading } from "@/design-system/Heading";
import { IconButton } from "@/design-system/IconButton";
import { NumericBadge } from "@/design-system/NumericBadge";
import { useTranslation } from "@/translations";

interface AskNobiChatHeaderProps {
  projects: SimpleProjectDto[];
  activeFilterCount: number;
  canFilter: boolean;
  hasSidebarContent: boolean;
  onOpenFilter: () => void;
  onOpenProjectsModal: () => void;
  onOpenMobileSidebar: () => void;
}

export function AskNobiChatHeader({
  projects,
  activeFilterCount,
  canFilter,
  hasSidebarContent,
  onOpenFilter,
  onOpenProjectsModal,
  onOpenMobileSidebar,
}: AskNobiChatHeaderProps): React.ReactNode {
  const { t } = useTranslation();

  const projectCount = projects.length;

  return (
    <header className="flex min-h-nobi-header shrink-0 flex-col items-center justify-between gap-space-sm gap-y-space-md border-b-hairline border-border-default p-space-md @md:flex-row">
      <div className="flex w-full min-w-0 flex-1 flex-row items-center gap-space-sm">
        {hasSidebarContent && (
          <div className="shrink-0 @md:hidden">
            <IconButton
              variant="ghostPrimary"
              size="sm"
              onClick={onOpenMobileSidebar}
              icon={<LayoutLeftIcon />}
              title={t("page.ask-nobi-chat.header.toggle-sidebar-btn")}
            />
          </div>
        )}
        <div className="min-w-0 flex-1 text-center @md:text-left">
          <Heading level={2} as="h1" overflow="wrap">
            {t("page.ask-nobi-chat.header.title")}
          </Heading>
        </div>
      </div>
      <nav
        aria-label={t("page.ask-nobi-chat.header.title")}
        className="flex h-control-md flex-wrap items-center justify-end gap-space-sm"
      >
        <Button icon={<InfoCircleIcon />} variant="ghostSecondary" emphasis={false} onClick={onOpenProjectsModal}>
          {t("page.ask-nobi-chat.header.projects-selected", { count: projectCount })}
        </Button>
        {canFilter && (
          <Button variant="soft" onClick={onOpenFilter} icon={<FilterFunnel02Icon size={16} />}>
            {t("page.ask-nobi-chat.header.filter-btn")}
            {activeFilterCount > 0 && <NumericBadge count={activeFilterCount} />}
          </Button>
        )}
      </nav>
    </header>
  );
}
